import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, LoaderCircle, AlertTriangle, Info, Edit2 } from 'lucide-react';
import ActionButton from '../../components/ActionButton.jsx';
import { sendAnalysisConfig, updateImprovingAgree } from '../../api/BackendApi.jsx';

const textIsListBrands = 'Vyjmenujte názvy produktů, značek';
const textIsNotListBrands = 'Vypište výhody, vlastnosti, asociace produktů, značek';

const ConfigSummaryStep = ({ question, isListBrands, setIsListBrands, fileId, onBack, onNext }) => {
  const navigate = useNavigate();

  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [improvingAgreeOrig, setImprovingAgreeOrig] = useState(false);
  const [improvingAgreeCur, setImprovingAgreeCur] = useState(false);
  
  const [summaryStatus, setSummaryStatus] = useState('loading'); // loading, success, error, insufficient_credit
  const [summaryData, setSummaryData] = useState({
    question: 'Načítání ...',
    isListBrands: 'Načítání ...',
    answers_count: 'Načítání ...'
  });
  const [errorMessage, setErrorMessage] = useState(null);
  const isModalNextEnabled = useMemo(() => {
    return isListBrands != null;
  }, [isListBrands]);


  useEffect(() => {
    let isMounted = true;
    
    const initializeFlow = async () => {
      try {
        const payload = {
          fileId: fileId,
          question: question,
        };
        
        const response = await sendAnalysisConfig(payload);
        
        if (!isMounted) return;

        if (!response.question || response.cost == null || response.answers_count == null) {
          throw new Error("Selhala komunikace se serverem.");
        }
      
        setSummaryData({
          question: response?.question || null,
          isListBrands: response?.is_list_brands,
          answers_count: response?.answers_count,
          credit_cost: response?.cost,
          credit_needed: response?.credit_needed,
          email: response?.email,
          is_temporary: response?.is_temporary
        });
        setIsListBrands(response.is_list_brands);
        setImprovingAgreeOrig(Boolean(response.improvingAgree));
        setImprovingAgreeCur(Boolean(response.improvingAgree));

        if (response.has_sufficient_credit) {
          setSummaryStatus('success'); 
        } else {
          setSummaryStatus('insufficient_credit');
        }

        window.dataLayer.push({
          event: 'step_summary',
          value: response?.cost,
          currency: 'CZK'
        });

      } catch (error) {
        if (!isMounted) return;
        console.error("Communication error:", error);
        setErrorMessage(error.message || "Selhala komunikace se serverem.");
        setSummaryStatus('error');
      }
    };

    initializeFlow();

    return () => { isMounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  const handleSaveQuestionType = async () => {
    setIsUpdating(true);
    setErrorMessage(null);

    try {
      const payload = {
        fileId: fileId,
        question: question,
        isListBrands: isListBrands,
      };
      
      const response = await sendAnalysisConfig(payload);

      if (!response.question || response.cost == null || response.answers_count == null) {
        throw new Error("Selhala komunikace se serverem.");
      }
      
      setIsListBrands(response.is_list_brands);

      setSummaryData({
        question: response?.question || null,
        isListBrands: response?.is_list_brands,
        answers_count: response?.answers_count,
        credit_cost: response?.cost,
        credit_needed: response?.credit_needed,
        email: response?.email,
        is_temporary: response?.is_temporary
      });

      if (response.has_sufficient_credit) {
        setSummaryStatus('success'); 
      } else {
        setSummaryStatus('insufficient_credit');
      }

      setShowTypeModal(false);

    } catch (error) {
      console.error("Update error:", error);
      setErrorMessage(error.message || "Selhala komunikace se serverem při aktualizaci typu otázky.");
      setShowTypeModal(false); 
      setSummaryStatus('error');
    } finally {
      setIsUpdating(false);
    }
  };

  const displayQuestionType = 
    isListBrands === true ? textIsListBrands : 
    isListBrands === false ? textIsNotListBrands : 
    'Načítání...';

  if (summaryStatus === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center w-full flex-grow min-h-[300px]">
        <LoaderCircle size={40} className="animate-spin text-blue-600 mb-4" />
        <p className="text-gray-600 font-medium">Zpracovávám zadání a ověřuji kredit...</p>
      </div>
    );
  }

  if (summaryStatus === 'error') {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center text-red-600">
        <XCircle size={32} />
        <p className="mt-4 text-lg font-medium">{errorMessage || 'Došlo k chybě.'}</p>
      </div>
    );
  }

  return (
    <div className="relative w-full flex flex-col items-center flex-grow">
      
      <div className="w-full text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800">Souhrn a platba</h2>
        <p className="">Krok 3</p>
      </div>

      <div className="w-full space-y-3">
        <div className="p-4 bg-gray-50 rounded-lg border">
          <p className="text-sm font-medium text-gray-500">Otázka:</p>
          <p className="font-semibold text-gray-800">{summaryData.question}</p>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg border flex justify-between items-center">
          <div>
            <p className="text-sm font-medium text-gray-500">Typ otázky:</p>
            <p className="font-semibold text-gray-800">{displayQuestionType}</p>
          </div>
          <button 
            onClick={() => setShowTypeModal(true)}
            className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 px-3 py-1.5 rounded-md transition-colors"
          >
            <Edit2 size={14} className="mr-1.5" />
            Změnit
          </button>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg border">
          <p className="text-sm font-medium text-gray-500">Počet odpovědí:</p>
          <p className="font-semibold text-gray-800">{summaryData.answers_count}</p>
        </div>
      </div>

      <div className="w-full mt-6">
        {summaryStatus === 'insufficient_credit' && summaryData.is_temporary === true && (
          <div>
            <div className="w-full text-center p-4 bg-red-50 border border-red-200 rounded-xl animate-fade-in">
              <AlertTriangle size={32} className="mx-auto text-red-500" />
              <h3 className="text-xl font-bold text-red-800 mt-3">Cena této analýzy je {summaryData?.credit_cost} Kč</h3>
            </div>
          </div>
        )}

        {summaryStatus === 'insufficient_credit' && summaryData.is_temporary !== true && (
          <div className="w-full text-center p-6 bg-red-50 border border-red-200 rounded-xl animate-fade-in">
            <AlertTriangle size={32} className="mx-auto text-red-500" />
            <h3 className="text-xl font-bold text-red-800 mt-3">Nedostatečný kredit</h3>
            <p className="text-gray-700 mt-2">
              Cena této analýzy je <span className="font-bold">{summaryData?.credit_cost} kreditů</span>.
              K dokončení Vám chybí <span className="font-bold">{summaryData?.credit_needed} kreditů</span>.
            </p>

            <button 
              onClick={() => navigate('/payments/buy', { state: { recommendedCredits: summaryData?.credit_needed, fileId: fileId } })}
              className="w-800 bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg shadow-md mt-3 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ease-in-out disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Dokoupit kredity
            </button>  
          </div>
        )}

        {summaryStatus === 'success' && (
          <div className="w-full text-center p-4 bg-green-50 border border-green-200 rounded-xl animate-fade-in">
            <CheckCircle size={32} className="mx-auto text-green-600" />
            <h3 className="text-xl font-bold text-green-800 mt-3">Vše je připraveno</h3>
            {summaryData.is_temporary === false && (
              <>
                <p className="text-gray-700 mt-2">
                  Cena této analýzy je <span className="font-bold">{summaryData?.credit_cost} kreditů</span>.
                </p>
                <p className="text-gray-600 text-sm mt-1">
                  Kredit bude odečten po potvrzení tohoto kroku.
                </p>
              </>
            )}
          </div>
        )}

        {/* Checkbox zpracování analýz */}
        {improvingAgreeOrig === false && summaryData.is_temporary !== true && (
          <div className="w-full flex flex-col items-center mt-8 mb-2">
            <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
              <input 
                type="checkbox" 
                className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                checked={improvingAgreeCur}
                onChange={(e) => {
                  setImprovingAgreeCur(e.target.checked);
                }}
              />
              <span>
                Souhlasím s využitím poskytnutých dat pro zlepšování výsledků v rámci mého uživatelského účtu.
              </span>
            </label>
          </div>
        )}

      </div>

      {/* Navigační tlačítka */}
      <div className="flex gap-4 w-full pt-6 mt-4 border-t border-gray-200">
        <button
          onClick={onBack}
          className="w-1/2 bg-white border border-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-lg hover:bg-gray-100 transition-colors"
        >
          Zpět
        </button>
        
        {summaryStatus === 'insufficient_credit' && summaryData.is_temporary === true ? (
          <ActionButton
            onClick={() => {
              navigate('/fill-invoice-data', { 
                state: { 
                  amount: summaryData?.credit_cost, 
                  fileId: fileId 
                } 
              });
            }} 
            className="w-1/2"
          >
            Pokračovat k platbě
          </ActionButton>
        ) : (
          <ActionButton
            onClick={() => {
              setShowConfirmModal(true); // Otevře modální okno místo okamžitého spuštění
            }} 
            disabled={summaryStatus !== 'success' }
            className="w-1/2"
          >
            Potvrdit a pokračovat
          </ActionButton>
        )}
      </div>

      {/* ----------------- MODÁLNÍ OKNO: POTVRZENÍ ODEČTENÍ KREDITŮ ----------------- */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 text-center animate-fade-in">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-50 mb-4">
              <Info className="h-6 w-6 text-blue-600" />
            </div>
            
            <h3 className="text-xl font-bold text-gray-800 mb-2">Potvrzení spuštění analýzy</h3>
            
            <p className="text-gray-600 mb-6">
              Opravdu si přejete pokračovat? Z vašeho účtu bude odečteno <span className="font-bold text-gray-900">{summaryData?.credit_cost} kreditů</span>.
            </p>

            <div className="flex gap-4 w-full">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="w-1/2 bg-white border border-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Zrušit
              </button>
              <ActionButton
                onClick={() => {
                  if (improvingAgreeCur !== improvingAgreeOrig) {
                    updateImprovingAgree(improvingAgreeCur);
                  }
                  setShowConfirmModal(false);
                  onNext(); // Spustí reálnou akci
                }}
                className="w-1/2"
              >
                Spustit analýzu
              </ActionButton>
            </div>
          </div>
        </div>
      )}

      {/* ----------------- MODÁLNÍ OKNO: VÝBĚR TYPU OTÁZKY ----------------- */}
      {showTypeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 animate-fade-in">
            <h3 className="text-xl font-bold text-gray-800 mb-2">Specifikujte typ otázky</h3>
            <p className="text-gray-600 mb-6 text-sm">
              Pro správné vyhodnocení analýzy potřebujeme vědět, na co se vaše otázka zaměřuje.
            </p>

            <div className="space-y-3 mb-4">
              <div 
                onClick={() => setIsListBrands(true)}
                className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${
                  isListBrands === true 
                    ? 'border-blue-600 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  checked={isListBrands === true}
                  onChange={() => setIsListBrands(true)}
                  className="h-5 w-5 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                />
                <label className="ml-3 block text-base font-medium text-gray-800 cursor-pointer w-full">
                  {textIsListBrands}
                </label>
              </div>

              <div 
                onClick={() => setIsListBrands(false)}
                className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${
                  isListBrands === false 
                    ? 'border-blue-600 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  checked={isListBrands === false}
                  onChange={() => setIsListBrands(false)}
                  className="h-5 w-5 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                />
                <label className="ml-3 block text-base font-medium text-gray-800 cursor-pointer w-full">
                  {textIsNotListBrands}
                </label>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-4">
              <ActionButton 
                onClick={handleSaveQuestionType} 
                disabled={!isModalNextEnabled || isUpdating}
                className="w-full sm:w-auto px-8"
              >
                {isUpdating ? 'Ukládám...' : 'Uložit nastavení'}
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigSummaryStep;